import Highcharts from 'highcharts';
import HighchartsStock from 'highcharts/highstock';
import HighchartsAccessibility from 'highcharts/modules/accessibility';

let initialized = false;
function ensureAccessibilityModule(): void {
    if (initialized) return;
    const factory: any =
        (HighchartsAccessibility as any)?.default ?? HighchartsAccessibility;
    if (typeof factory !== 'function') return;
    factory(Highcharts);
    if (HighchartsStock && HighchartsStock !== Highcharts) {
        factory(HighchartsStock);
    }
    initialized = true;
}
ensureAccessibilityModule();

const SCREEN_READER_FORMAT =
    '<h5>{chartTitle}</h5>' +
    '<div>{typeDescription}</div>' +
    '<div>{chartLongdesc}</div>' +
    '<div>{xAxisDescription}</div>' +
    '<div>{yAxisDescription}</div>';

export interface ChartA11yInput {
    /**
     * Short accessible name for the chart, equivalent to an <img alt="..."> attribute.
     * Read by screen readers as the chart's name via the SVG <title> element.
     */
    altText: string;
    /**
     * Optional longer description, equivalent to longdesc / <desc>. Defaults to altText.
     */
    description?: string;
    pointDescriptionFormatter?: (point: any) => string;
}

export interface ChartA11yOptions {
    title: { text: string; style: { display: string } };
    accessibility: {
        enabled: boolean;
        description: string;
        screenReaderSection: { beforeChartFormat: string };
        keyboardNavigation: { enabled: boolean };
        point?: {
            descriptionFormatter: (point: any) => string;
        };
    };
    chartDescription: string;
}

/**
 * Builds the accessibility-related Highcharts option fragments for a chart.
 *
 * Usage:
 *   const a11y = getChartA11yOptions({ altText: 'AUM trend chart' });
 *   const options = {
 *       chart: { ...other, description: a11y.chartDescription },
 *       title: a11y.title,
 *       accessibility: a11y.accessibility,
 *       ...
 *   };
 */
export function getChartA11yOptions({
    altText,
    description,
    pointDescriptionFormatter,
}: ChartA11yInput): ChartA11yOptions {
    ensureAccessibilityModule();
    const longDesc = description ?? altText;
    const result: ChartA11yOptions = {
        title: {
            text: altText,
            // Visually hidden, but still used as the chart's accessible name
            // in the SVG <title> element and the a11y screen-reader region.
            style: { display: 'none' },
        },
        accessibility: {
            enabled: true,
            description: longDesc,
            screenReaderSection: {
                beforeChartFormat: SCREEN_READER_FORMAT,
            },
            keyboardNavigation: { enabled: true },
        },
        chartDescription: longDesc,
    };
    if (pointDescriptionFormatter) {
        result.accessibility.point = { descriptionFormatter: pointDescriptionFormatter };
    }
    return result;
}
